import React, { useState, useEffect, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ShieldCheck, 
  ShieldAlert,
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Zap,
  Lock,
  RefreshCw,
  ArrowRight,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Unified Effects & Enforcement Panel
 * 
 * Shows the relationship: States → Effects → Capabilities → Enforcement
 * in one clear, intuitive flow.
 */
const EnforcementPanel = ({ machineId, machine, capabilities }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState('simulation');
  const [expandedEffects, setExpandedEffects] = useState({});

  const fetchReport = useCallback(async () => {
    if (!machineId) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/machines/${machineId}/enforce/report`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profile, level: 'strict' })
        }
      );
      if (response.ok) {
        setReport(await response.json());
      }
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  }, [machineId, profile]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const toggleEffect = (name) => {
    setExpandedEffects(prev => ({ ...prev, [name]: !prev[name] }));
  };

  if (!machineId || !machine) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <div className="text-center text-gray-500">
          <Zap className="w-6 h-6 mx-auto mb-2 opacity-50" />
          <p className="text-xs">Select a machine to view effects</p>
        </div>
      </div>
    );
  }

  const effects = machine.effects ? Object.entries(machine.effects) : [];
  const guards = machine.guards ? Object.entries(machine.guards) : [];
  const missing = new Set(report?.missing_capabilities || []);
  const canExecute = report?.can_execute_fully ?? true;

  // Build: which states use which effects
  const effectUsage = {};
  if (machine.states) {
    Object.entries(machine.states).forEach(([sid, state]) => {
      (state.entry || []).forEach(e => {
        if (!effectUsage[e]) effectUsage[e] = [];
        effectUsage[e].push({ state: sid, type: 'entry' });
      });
      (state.exit || []).forEach(e => {
        if (!effectUsage[e]) effectUsage[e] = [];
        effectUsage[e].push({ state: sid, type: 'exit' });
      });
      (state.transitions || []).forEach(t => {
        (t.effects || []).forEach(e => {
          if (!effectUsage[e]) effectUsage[e] = [];
          effectUsage[e].push({ state: sid, type: 'transition', event: t.event });
        });
      });
    });
  }

  return (
    <div className="h-full flex flex-col" data-testid="enforcement-panel">
      {/* Status Bar */}
      <div className={cn(
        "px-3 py-2 flex items-center justify-between border-b",
        canExecute
          ? "bg-green-500/10 border-green-500/20"
          : "bg-orange-500/10 border-orange-500/20"
      )}>
        <div className="flex items-center space-x-2 text-xs">
          {canExecute ? (
            <><ShieldCheck className="w-4 h-4 text-green-400" /><span className="text-green-400 font-medium">All effects executable</span></>
          ) : (
            <><ShieldAlert className="w-4 h-4 text-orange-400" /><span className="text-orange-400 font-medium">{missing.size} missing cap{missing.size !== 1 ? 's' : ''}</span></>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Select value={profile} onValueChange={setProfile}>
            <SelectTrigger className="h-6 w-28 text-[10px] border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="simulation">Simulation</SelectItem>
              <SelectItem value="offline_browser">Offline</SelectItem>
              <SelectItem value="online_browser">Online</SelectItem>
              <SelectItem value="worker">Worker</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={fetchReport} disabled={loading} className="h-6 w-6 p-0">
            <RefreshCw className={cn("w-3 h-3", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">

          {/* === Effects Section === */}
          {effects.length > 0 ? (
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                <Zap className="w-3 h-3 mr-1.5 text-yellow-500" />
                Effects ({effects.length})
              </div>
              <div className="space-y-1.5">
                {effects.map(([name, effect]) => {
                  const caps = effect.required_capabilities || [];
                  const missingCaps = caps.filter(c => missing.has(c.name));
                  const ok = missingCaps.length === 0;
                  const usage = effectUsage[name] || [];
                  const isExpanded = expandedEffects[name];

                  return (
                    <div key={name} className={cn(
                      "border rounded overflow-hidden",
                      ok ? "border-white/10" : "border-orange-500/30"
                    )}>
                      {/* Effect Header */}
                      <button
                        onClick={() => toggleEffect(name)}
                        className={cn(
                          "w-full flex items-center justify-between px-2.5 py-2 text-left transition-colors",
                          ok ? "bg-white/5 hover:bg-white/10" : "bg-orange-500/5 hover:bg-orange-500/10"
                        )}
                      >
                        <div className="flex items-center space-x-2 min-w-0">
                          {ok ? (
                            <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                          )}
                          <span className="font-mono text-xs font-medium truncate">{name}</span>
                        </div>
                        <div className="flex items-center space-x-1.5 flex-shrink-0">
                          {caps.length > 0 && (
                            <Badge variant="outline" className="text-[9px] h-4 px-1.5">
                              {caps.length} cap{caps.length !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          {usage.length > 0 && (
                            <Badge variant="outline" className="text-[9px] h-4 px-1.5 text-blue-400 border-blue-500/30">
                              {usage.length} use{usage.length !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          {isExpanded ? <ChevronDown className="w-3 h-3 text-gray-500" /> : <ChevronRight className="w-3 h-3 text-gray-500" />}
                        </div>
                      </button>

                      {/* Expanded Detail */}
                      {isExpanded && (
                        <div className="px-2.5 py-2 bg-black/20 border-t border-white/5 space-y-2">
                          {/* Type Signature */}
                          <div className="font-mono text-[10px] text-gray-400">
                            <span className="text-blue-400">{effect.input_type}</span>
                            <ArrowRight className="w-3 h-3 inline mx-1 text-gray-600" />
                            <span className="text-green-400">{effect.output_type}</span>
                            {effect.failure_type && effect.failure_type !== 'never' && (
                              <span className="text-red-400 ml-2">throws {effect.failure_type}</span>
                            )}
                          </div>

                          {/* Required Capabilities */}
                          {caps.length > 0 && (
                            <div>
                              <div className="text-[9px] text-gray-500 mb-1">Requires:</div>
                              <div className="flex flex-wrap gap-1">
                                {caps.map((cap, i) => {
                                  const isMissing = missing.has(cap.name);
                                  return (
                                    <Badge key={i} variant="outline" className={cn(
                                      "text-[9px] h-4 px-1.5 font-mono",
                                      isMissing ? "text-red-400 border-red-500/30 bg-red-500/10" : "text-green-400 border-green-500/30"
                                    )}>
                                      {isMissing && <Lock className="w-2 h-2 mr-0.5" />}
                                      {cap.name}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Where used */}
                          {usage.length > 0 && (
                            <div>
                              <div className="text-[9px] text-gray-500 mb-1">Used in:</div>
                              <div className="space-y-0.5">
                                {usage.map((u, i) => (
                                  <div key={i} className="text-[10px] font-mono text-gray-400">
                                    <span className="text-white">{u.state}</span>
                                    <span className="text-gray-600 mx-1">/</span>
                                    <span className="text-gray-500">{u.type}</span>
                                    {u.event && <span className="text-blue-400 ml-1">({u.event})</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-xs text-gray-500">
              <Zap className="w-5 h-5 mx-auto mb-1 opacity-40" />
              No effects defined
            </div>
          )}

          {/* === Guards Section === */}
          {guards.length > 0 && (
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center">
                <Lock className="w-3 h-3 mr-1.5 text-cyan-500" />
                Guards ({guards.length})
              </div>
              <div className="space-y-1">
                {guards.map(([name, guard]) => (
                  <div key={name} className="px-2.5 py-1.5 bg-white/5 border border-white/10 rounded flex items-center justify-between">
                    <span className="font-mono text-xs">{name}</span>
                    <span className="font-mono text-[10px] text-gray-500 truncate ml-2 max-w-[150px]">
                      {guard.predicate}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* === Missing Capabilities (if any) === */}
          {missing.size > 0 && (
            <div className="p-2.5 bg-red-500/10 border border-red-500/30 rounded">
              <div className="flex items-center text-xs text-red-400 font-medium mb-2">
                <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                Missing Capabilities
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[...missing].map(cap => (
                  <Badge key={cap} variant="destructive" className="text-[10px] h-5 font-mono">
                    <Lock className="w-2.5 h-2.5 mr-1" />
                    {cap}
                  </Badge>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mt-2">
                Switch to a runtime profile that grants these capabilities, or remove the effects that require them.
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default EnforcementPanel;
